import { useEffect, useMemo, useRef } from "react";

interface CommentLocation {
  type: "comment" | "reply";
  parentId: number | null;
  commentId: number;
}

interface CommentLike {
  id: number | string;
  comments?: Array<{ id: number | string }>;
}

interface UseCommentHighlightingProps {
  highlightCommentIds: number[];
  allComments: CommentLike[];
  isExpanded: boolean;
  hasMoreComments: boolean;
  isLoadingMoreComments: boolean;
  handleLoadMoreComments: () => Promise<void>;
  expandAllComments: () => void;
  expandRepliesForComment: (commentId: number) => void;
  shouldScrollToComment: boolean;
}

export const useCommentHighlighting = ({
  highlightCommentIds,
  allComments,
  isExpanded,
  hasMoreComments,
  isLoadingMoreComments,
  handleLoadMoreComments,
  expandAllComments,
  expandRepliesForComment,
  shouldScrollToComment,
}: UseCommentHighlightingProps) => {
  const commentLocationById = useMemo(() => {
    const locations = new Map<number, CommentLocation>();

    for (const comment of allComments) {
      const commentId = Number(comment.id);
      if (Number.isNaN(commentId)) continue;

      locations.set(commentId, {
        type: "comment",
        parentId: null,
        commentId,
      });

      for (const reply of comment.comments ?? []) {
        const replyId = Number(reply.id);
        if (Number.isNaN(replyId)) continue;

        locations.set(replyId, {
          type: "reply",
          parentId: commentId,
          commentId: replyId,
        });
      }
    }

    return locations;
  }, [allComments]);

  const highlightLocation = useMemo(() => {
    if (highlightCommentIds.length === 0) return null;

    for (const targetId of highlightCommentIds) {
      const location = commentLocationById.get(targetId);
      if (location) return location;
    }

    return null;
  }, [highlightCommentIds, commentLocationById]);

  const autoExpandedHighlightKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightLocation) {
      autoExpandedHighlightKeyRef.current = null;
      return;
    }

    const highlightKey = `${highlightLocation.type}:${highlightLocation.parentId ?? "none"}:${highlightLocation.commentId}`;
    if (autoExpandedHighlightKeyRef.current === highlightKey) return;
    autoExpandedHighlightKeyRef.current = highlightKey;

    if (!isExpanded) {
      expandAllComments();
    }

    if (highlightLocation.type === "reply" && highlightLocation.parentId) {
      expandRepliesForComment(highlightLocation.parentId);
    }
  }, [
    highlightLocation,
    isExpanded,
    expandAllComments,
    expandRepliesForComment,
  ]);

  useEffect(() => {
    if (highlightCommentIds.length === 0) return;
    if (!hasMoreComments || isLoadingMoreComments) return;
    if (highlightLocation) return;

    void handleLoadMoreComments();
  }, [
    highlightCommentIds,
    hasMoreComments,
    isLoadingMoreComments,
    highlightLocation,
    handleLoadMoreComments,
  ]);

  const lastScrolledIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!highlightLocation || !shouldScrollToComment) return;

    const targetCommentId = highlightLocation.commentId;
    if (lastScrolledIdRef.current === targetCommentId) return;

    const target = document.querySelector<HTMLElement>(
      `[data-comment-id="${targetCommentId}"]`,
    );
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    lastScrolledIdRef.current = targetCommentId;
  }, [highlightLocation, shouldScrollToComment]);
};
